import React, { useState, useEffect } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import './App.css';
import { MdAddCircleOutline, MdPerson, MdLocationOn, MdHome } from 'react-icons/md';
import IconButton from '@mui/material/IconButton';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import Home from './pages/Home';
import MyAds from './pages/MyAds';
import Login from './pages/Login';
import PostAd from './pages/PostAd';
import UserProfile from './pages/UserProfile';
import AdDetailsPage from './pages/AdDetailsPage';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Footer from './components/Footer';

function App() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState('');
  const [profileLoaded, setProfileLoaded] = useState(false);

  useEffect(() => {
    import('./api').then(api => {
      api.default.get('/cities/').then(res => {
        setCities(res.data);
        // After cities loaded, fetch user profile if logged in
        const token = localStorage.getItem('token');
        if (token) {
          api.default.get('/profile/').then(profileRes => {
            if (profileRes.data.default_city) {
              setSelectedCity(profileRes.data.default_city);
            } else {
              const tehran = res.data.find(city => city.name === 'تهران' || city.name === 'Tehran');
              setSelectedCity(tehran ? tehran.id : res.data[0]?.id);
            }
            setProfileLoaded(true);
          }).catch(() => {
            const tehran = res.data.find(city => city.name === 'تهران' || city.name === 'Tehran');
            setSelectedCity(tehran ? tehran.id : res.data[0]?.id);
            setProfileLoaded(true);
          });
        } else {
          const tehran = res.data.find(city => city.name === 'تهران' || city.name === 'Tehran');
          setSelectedCity(tehran ? tehran.id : res.data[0]?.id);
          setProfileLoaded(true);
        }
      });
    });
  }, []);

  useEffect(() => {
    setIsAuthenticated(!!localStorage.getItem('user'));
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    window.location.href = '/';
  };

  // Update default_city in profile when user changes city (if authenticated)
  const handleCityChange = e => {
    const newCity = e.target.value;
    setSelectedCity(newCity);
    const token = localStorage.getItem('token');
    if (token) {
      import('./api').then(api => {
        api.default.patch('/profile/', { default_city: newCity })
          .catch(() => {});
      });
    }
  };

  if (!profileLoaded) return null;

  return (
    <Router>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', direction: 'rtl', fontFamily: 'Vazirmatn', display: 'flex', flexDirection: 'column' }}>
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            bgcolor: '#ffffff',
            color: '#1a1a2e',
            borderBottom: '1px solid #e8edf4',
            boxShadow: '0 2px 20px rgba(25, 118, 210, 0.07)',
            zIndex: 100,
          }}
        >
          {/* Thin accent bar at very top */}
          <Box sx={{ height: 3, background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #1976d2 100%)' }} />

          <Toolbar
            sx={{
              px: { xs: 1.5, sm: 4 },
              minHeight: '68px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: { xs: 1, sm: 2 },
            }}
          >
            {/* Left side: logo + tagline + city */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>

              {/* Logo block */}
              <a href="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Box sx={{
                  width: { xs: 32, sm: 38 },
                  height: { xs: 32, sm: 38 },
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 3px 10px rgba(25,118,210,0.3)',
                  flexShrink: 0,
                }}>
                  <span style={{ color: 'white', fontWeight: 900, fontSize: 18, fontFamily: 'Vazirmatn' }}>آ</span>
                </Box>
                <Box>
                  <Typography sx={{
                    fontFamily: 'Vazirmatn',
                    fontWeight: 900,
                    fontSize: { xs: '1.05rem', sm: '1.35rem' },
                    color: '#1976d2',
                    lineHeight: 1.1,
                    letterSpacing: '0.02em',
                  }}>
                    آگه‌جو
                  </Typography>
                  <Typography sx={{
                    display: { xs: 'none', sm: 'block' },
                    fontFamily: 'Vazirmatn',
                    fontSize: '0.65rem',
                    color: '#94a3b8',
                    lineHeight: 1,
                    letterSpacing: '0.03em',
                    fontWeight: 500,
                  }}>
                    بازار آگهی آنلاین
                  </Typography>
                </Box>
              </a>

              {/* Divider — desktop only */}
              <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' }, mx: 0.5, borderColor: '#e2e8f0', height: 32, alignSelf: 'center' }} />

              {/* City selector */}
              {window.location.pathname !== '/login' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <MdLocationOn size={18} color="#1976d2" style={{ flexShrink: 0 }} />
                  <select
                    value={selectedCity}
                    onChange={handleCityChange}
                    style={{
                      fontFamily: 'Vazirmatn',
                      fontWeight: 600,
                      fontSize: 'clamp(12px, 2.6vw, 15px)',
                      padding: '6px 10px 6px 28px',
                      borderRadius: 8,
                      border: '1.5px solid #e2e8f0',
                      background: '#f8fafc',
                      color: '#334155',
                      outline: 'none',
                      minWidth: 90,
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      backgroundImage:
                        "url('data:image/svg+xml;utf8,<svg fill=\"%231976d2\" height=\"20\" viewBox=\"0 0 24 24\" width=\"20\" xmlns=\"http://www.w3.org/2000/svg\"><path d=\"M7 10l5 5 5-5z\"/></svg>')",
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'left 6px center',
                      cursor: 'pointer',
                      transition: 'border-color 0.2s',
                    }}
                  >
                    {cities.map(city => (
                      <option key={city.id} value={city.id}>{city.name}</option>
                    ))}
                  </select>
                </Box>
              )}
            </Box>

            {/* Right side: action buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1.5 } }}>
              <IconButton
                href="/"
                sx={{ color: '#64748b', '&:hover': { color: '#1976d2', bgcolor: '#eff6ff' }, borderRadius: 2 }}
                title="خانه"
              >
                <MdHome size={22} />
              </IconButton>
              {window.location.pathname !== '/login' && (
                <>
                  {isAuthenticated && (
                    <Button
                      href="/post-ad"
                      startIcon={<MdAddCircleOutline size={18} />}
                      variant="contained"
                      sx={{
                        background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                        color: 'white',
                        borderRadius: 99,
                        px: { xs: 1.5, sm: 2.5 },
                        py: { xs: 0.9, sm: 1.1 },
                        fontSize: { xs: '0.72rem', sm: '0.875rem' },
                        fontFamily: 'Vazirmatn',
                        fontWeight: 700,
                        boxShadow: '0 3px 12px rgba(34, 197, 94, 0.3)',
                        textTransform: 'none',
                        whiteSpace: 'nowrap',
                        '&:hover': { boxShadow: '0 4px 16px rgba(34,197,94,0.45)' },
                        '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 }, ml: 0 },
                      }}
                    >
                      <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>ثبت آگهی جدید</Box>
                    </Button>
                  )}

                  {isAuthenticated ? (
                    <>
                      <Button
                        startIcon={<MdPerson size={18} />}
                        variant="outlined"
                        sx={{
                          borderColor: '#cbd5e1',
                          color: '#334155',
                          borderRadius: 99,
                          px: { xs: 1.5, sm: 2.5 },
                          py: { xs: 0.9, sm: 1.1 },
                          fontSize: { xs: '0.72rem', sm: '0.875rem' },
                          fontFamily: 'Vazirmatn',
                          fontWeight: 600,
                          textTransform: 'none',
                          whiteSpace: 'nowrap',
                          bgcolor: '#f8fafc',
                          '&:hover': { borderColor: '#1976d2', color: '#1976d2', bgcolor: '#eff6ff' },
                          '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 }, ml: 0 },
                        }}
                        onClick={e => setAnchorEl(e.currentTarget)}
                      >
                        <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>حساب کاربری</Box>
                      </Button>

                      <Menu
                        anchorEl={anchorEl}
                        open={!!anchorEl}
                        onClose={() => setAnchorEl(null)}
                        PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 8px 30px rgba(0,0,0,0.12)', mt: 1, minWidth: 160 } }}
                      >
                        <MenuItem onClick={() => { setAnchorEl(null); window.location.href = '/profile'; }} sx={{ fontFamily: 'Vazirmatn', gap: 1 }}>
                          <span>👤</span> پروفایل من
                        </MenuItem>
                        <MenuItem onClick={() => { setAnchorEl(null); window.location.href = '/my-ads'; }} sx={{ fontFamily: 'Vazirmatn', gap: 1 }}>
                          <span>📋</span> آگهی‌های من
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={() => { setAnchorEl(null); handleLogout(); }} sx={{ fontFamily: 'Vazirmatn', color: 'error.main', gap: 1 }}>
                          <span>🚪</span> خروج
                        </MenuItem>
                      </Menu>
                    </>
                  ) : (
                    <Button
                      href="/login"
                      startIcon={<MdPerson size={18} />}
                      variant="contained"
                      sx={{
                        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                        color: 'white',
                        borderRadius: 99,
                        px: { xs: 1.5, sm: 2.5 },
                        py: { xs: 0.9, sm: 1.1 },
                        fontSize: { xs: '0.72rem', sm: '0.875rem' },
                        fontFamily: 'Vazirmatn',
                        fontWeight: 700,
                        boxShadow: '0 3px 12px rgba(25,118,210,0.3)',
                        textTransform: 'none',
                        whiteSpace: 'nowrap',
                        '&:hover': { boxShadow: '0 4px 16px rgba(25,118,210,0.45)' },
                        '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 }, ml: 0 },
                      }}
                    >
                      <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>ورود / ثبت‌نام</Box>
                    </Button>
                  )}
                </>
              )}
            </Box>
          </Toolbar>
        </AppBar>

        <Toolbar sx={{ minHeight: '71px !important' }} />
        <Box component="main" sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', pt: 4, pb: 6 }}>
          <Container maxWidth="lg" sx={{ px: 2 }}>
            <Paper elevation={0} sx={{ borderRadius: 0, p: 0, border: 'none', boxShadow: 'none', bgcolor: 'transparent' }}>
              <Routes>
                <Route path="/" element={<Home selectedCity={selectedCity} />} />
                <Route path="/login" element={<Login />} />
                <Route path="/post-ad" element={<PostAd selectedCity={selectedCity} />} />
                <Route path="/profile" element={<UserProfile />} />
                <Route path="/my-ads" element={<MyAds />} />
                <Route path="/ads/:id" element={<AdDetailsPage />} />
              </Routes>
            </Paper>
          </Container>
        </Box>
        <Footer />
      </Box>
    </Router>
  );
}

export default App;
